import DrbfmFirstRunInputForm from '@components/drbfm/first-run-input/DrbfmFirstRunInputForm';
import { useFirstRunInputForm } from '@components/drbfm/first-run-input/useFirstRunInputForm';
import RegisteredDocumentSource from '@components/drbfm/first-run-input/RegisteredDocumentSource';
import { registeredSubmission } from '@services/drbfm.firstRunSubmission';
import type { FirstRunInputSubmission } from '@components/drbfm/first-run-input/types';
/**
 * DRBFM Analysis Page
 *
 * Main page for DRBFM (Design Review Based on Failure Mode) analysis.
 * Implements a wizard-based workflow for design change analysis.
 */

import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Stepper } from '@/components/ui/stepper';
import { useAppDispatch, useAppSelector } from '@store/index';
import { useDRBFM } from '@hooks/useDRBFM';
import { useConfirm } from '@hooks/useConfirm';
import { selectCadEnabled } from '@features/featureFlags/featureFlagsSlice';
import { setAnalysisResults } from '@features/drbfm/drbfmSlice';
import { drbfmService } from '@services/drbfm.service';
import { sessionResultsPath } from '@components/drbfm/history/sessionNavigation';
import DRBFMInputStep from '@components/drbfm/steps/DRBFMInputStep';
import DRBFMProcessingStep from '@components/drbfm/steps/DRBFMProcessingStep';
import DRBFMResultsStep from '@components/drbfm/steps/DRBFMResultsStep';
import DRBFMCaseWorkflow from '@components/drbfm/case/DRBFMCaseWorkflow';
import DRBFMMainlineWorkflow from '@components/drbfm/workflow/DRBFMMainlineWorkflow';
import { useMainlineStatus } from '@components/drbfm/workflow/mainline/useMainlineStatus';
import DRBFMStagedWorkflow from '@components/drbfm/workflow/DRBFMStagedWorkflow';
import DRBFMPageHeader from '@components/drbfm/analysis-page/DRBFMPageHeader';
import SessionCreatingOverlay from '@components/drbfm/analysis-page/SessionCreatingOverlay';
import { useDrbfmSessionAutoLoad } from '@components/drbfm/analysis-page/useDrbfmSessionAutoLoad';
import { WIZARD_STEPS, getStepIndex } from '@components/drbfm/analysis-page/wizardSteps';
import DrbfmPreviewDialog from '@components/drbfm/preview/DrbfmPreviewDialog';
import WorkspaceOutboundActions, { anchoredCadModel } from '@components/drbfm/analysis-page/WorkspaceOutboundActions';
import { useDrbfmHtmlPreview } from '@components/drbfm/preview/useDrbfmHtmlPreview';
import type {
  PredefinedPartLabel,
  UpdateDesignChangeReviewRequest,
  UpdateFailureModeReviewRequest,
} from '../types';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import { useTenantContext } from '@hooks/useTenantContext';
import { CUSTOMER_DEMO_TENANT_ID } from '@constants/tenantProfiles';

// Lazy: the results workspace is only for ?results=1, so it stays out of the initial bundle.
const ResultsWorkspace = React.lazy(() => import('@components/drbfm/results-workspace/ResultsWorkspace'));

const DRBFMPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cadEnabled = useAppSelector(selectCadEnabled);
  const { tenantId } = useTenantContext();
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');
  const modeParam = searchParams.get('mode');
  const mainlineOpen = searchParams.get('edit') === '1';
  // The #1592 results workspace for an initialized mainline session; the older editor stays on ?edit=1.
  const resultsOpen = searchParams.get('results') === '1' && !mainlineOpen;
  const mainlineStatus = useMainlineStatus(tenantId);
  const mainlineEnabled = mainlineStatus === 'enabled';
  const advancedModesEnabled = tenantId !== CUSTOMER_DEMO_TENANT_ID;
  const isCaseMode = advancedModesEnabled && modeParam === 'case';
  // URL is the source of truth, including browser Back/Forward navigation.
  const workflowMode = modeParam === 'staged' ? 'staged' : 'quick';
  const [stagedHasContent, setStagedHasContent] = React.useState(false);
  const [caseInProgress, setCaseInProgress] = React.useState(false);
  const confirm = useConfirm();
  const userId = useAppSelector(state => state.auth.user?.id);
  const firstRunForm = useFirstRunInputForm(`${tenantId}:${userId}:${sessionStorage.getItem('auth_session_id')}`);
  const [firstRunError, setFirstRunError] = React.useState<string | null>(null);
  const resetFirstRun = firstRunForm.reset;
  React.useEffect(() => { resetFirstRun(); setFirstRunError(null); }, [tenantId, userId, resetFirstRun]);
  React.useEffect(() => { if (resultsOpen) resetFirstRun(); }, [resultsOpen, resetFirstRun]);
  const [isLinkingCad, setIsLinkingCad] = React.useState(false);
  const [linkCadError, setLinkCadError] = React.useState<string | null>(null);
  const preview = useDrbfmHtmlPreview<{ id: string }>();
  const {
    currentSession,
    wizardStep,
    inputData,
    analysisProgress,
    analysisResults,
    isLoading,
    isAnalyzing,
    error,
    canStartAnalysis,
    isExporting,
    exportError,
    createSessionAndAnalyze,
    updateInput,
    goToStep,
    reset,
    exportExcel,
    cancelAnalysis,
    completionPending,
    retryCompletion,
    clearSourceError,
  } = useDRBFM({ initializeWorkspace: mainlineEnabled });

  const sessionAutoLoadRef = useDrbfmSessionAutoLoad({ sessionIdParam, isCaseMode: isCaseMode || mainlineOpen || resultsOpen || workflowMode === 'staged', reset });

  const steps = useMemo(
    () => WIZARD_STEPS.map((step) => ({ id: step.id, label: t(step.labelKey) })),
    [t],
  );
  const activeStep = getStepIndex(wizardStep);
  const isQuickProcessing =
    !isCaseMode &&
    !mainlineOpen &&
    !resultsOpen &&
    workflowMode === 'quick' &&
    wizardStep === 'processing';

  const handleBackClick = () => {
    if (sessionIdParam || wizardStep === 'results') {
      navigate('/drbfm/history');
      return;
    }
    navigate('/');
  };

  const hasQuickInput = Boolean(
    inputData.itemName?.trim()
    || inputData.title?.trim()
    || inputData.oldSpecification?.trim()
    || inputData.newSpecification?.trim(),
  );

  // Leaving case mode lands on bare /drbfm, where the auto-loader resets the
  // quick wizard, and unmounts the case workflow with it.
  const handleCaseModeClick = async () => {
    if (isCaseMode && (hasQuickInput || caseInProgress) && !(await confirm({
      title: t('drbfm.confirm.normalMode.title'),
      description: t('drbfm.confirm.normalMode.description'),
      confirmLabel: t('drbfm.confirm.normalMode.confirm'),
    }))) return;
    navigate(isCaseMode ? '/drbfm' : '/drbfm?mode=case');
  };

  // Only the staged run dies here: the quick wizard keeps its state in Redux.
  const handleWorkflowModeChange = async (mode: 'quick' | 'staged') => {
    if (mode === workflowMode) return;
    if (workflowMode === 'staged' && stagedHasContent && !(await confirm({
      title: t('drbfm.confirm.switchWorkflowMode.title'),
      description: t('drbfm.confirm.switchWorkflowMode.description'),
      confirmLabel: t('drbfm.confirm.switchWorkflowMode.confirm'),
    }))) return;
    navigate(mode === 'staged' ? '/drbfm?mode=staged' : '/drbfm');
  };

  const handleStartAnalysis = async () => {
    const session = await createSessionAndAnalyze();
    if (session) {
      const workspace = mainlineEnabled && (inputData.analysisRequest?.sources
        || (inputData.specOldDocumentId && inputData.specNewDocumentId));
      navigate(`/drbfm?session=${session.id}${workspace ? '&results=1' : ''}`, { replace: true });
    }
  };

  const handleRegisteredStart = async (submission: FirstRunInputSubmission) => {
    setFirstRunError(null);
    try {
      const session = await createSessionAndAnalyze(registeredSubmission(submission));
      if (session) navigate(`/drbfm?session=${session.id}&results=1`, { replace: true });
    } catch {
      setFirstRunError(t('drbfm.firstRun.registeredOnly'));
    }
  };

  const handleRetryCompletion = async () => {
    if (!currentSession || !(await retryCompletion())) return;
    const session = await drbfmService.getSession(currentSession.id).catch(() => null);
    navigate(sessionResultsPath(currentSession.id, session, mainlineEnabled), { replace: true });
  };

  const handleCancelAnalysis = () => {
    cancelAnalysis();
    goToStep('input');
  };

  const handleNewAnalysis = () => {
    reset();
    resetFirstRun();
    // Drop the historical session param so a refresh after this point
    // doesn't pull the user back into the previous session.
    if (sessionIdParam) {
      sessionAutoLoadRef.current = null;
      navigate('/drbfm', { replace: true });
    }
  };

  const handleExport = async () => {
    await exportExcel();
  };

  const handlePreview = async () => {
    const sessionId = analysisResults?.session?.id;
    if (!sessionId) return;
    await preview.open({ id: sessionId });
  };

  const handleLinkCad = async (
    cadModelId: string,
    predefinedPartLabels?: PredefinedPartLabel[],
  ) => {
    const sessionId = analysisResults?.session?.id;
    if (!sessionId) return;

    setIsLinkingCad(true);
    setLinkCadError(null);
    try {
      const response = await drbfmService.linkCad(sessionId, { cadModelId, predefinedPartLabels });
      const refreshed = await drbfmService.getSessionResults(sessionId);
      dispatch(setAnalysisResults(refreshed));
      if (response.unresolvedFailureModeIds.length > 0) {
        setLinkCadError(
          t('drbfm.cadLink.partialResolved', {
            linked: response.linkedCount,
            unresolved: response.unresolvedFailureModeIds.length,
          }),
        );
      }
    } catch (err) {
      setLinkCadError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.errors.cadLinkFailed' }));
    } finally {
      setIsLinkingCad(false);
    }
  };

  const handleReviewDesignChange = async (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => {
    const sessionId = analysisResults?.session?.id;
    if (!sessionId || !analysisResults) return;
    const updated = await drbfmService.updateDesignChangeReview(
      sessionId,
      designChangeId,
      request,
    );
    dispatch(setAnalysisResults({
      ...analysisResults,
      designChanges: analysisResults.designChanges.map((change) => (
        change.id === designChangeId ? updated : change
      )),
    }));
  };

  const handleReviewFailureMode = async (
    failureModeId: string,
    request: UpdateFailureModeReviewRequest,
  ) => {
    const sessionId = analysisResults?.session?.id;
    if (!sessionId || !analysisResults) return;
    const updated = await drbfmService.updateFailureModeReview(
      sessionId,
      failureModeId,
      request,
    );
    dispatch(setAnalysisResults({
      ...analysisResults,
      failureModes: analysisResults.failureModes.map((failureMode) => (
        failureMode.id === failureModeId ? updated : failureMode
      )),
    }));
  };

  const handlePreviewFailureModeRevision = async (
    failureModeId: string,
    instruction: string,
  ) => {
    const sessionId = analysisResults?.session?.id;
    if (!sessionId) throw new Error('DRBFM session is not loaded');
    return drbfmService.previewFailureModeRevision(sessionId, failureModeId, instruction);
  };

  const renderStepContent = () => {
    switch (wizardStep) {
      case 'input':
        if (mainlineEnabled && searchParams.get('input') !== 'legacy') return (
          <>
            {firstRunError && <p role="alert" className="mb-4 text-destructive">{firstRunError}</p>}
            <DrbfmFirstRunInputForm form={firstRunForm} onStart={handleRegisteredStart}
              isStarting={isLoading} disabled={completionPending || isAnalyzing} cadEnabled={false}
              renderDocumentSource={props => <RegisteredDocumentSource {...props} onChange={document => {
                props.onChange(document);
                if (document) clearSourceError();
              }} />} />
          </>
        );
        return (
          <DRBFMInputStep
            inputData={inputData}
            onInputChange={updateInput}
            onStartAnalysis={handleStartAnalysis}
            canStart={canStartAnalysis && !completionPending}
            isLoading={isLoading}
            cadEnabled={cadEnabled}
          />
        );

      case 'processing':
        return (
          <DRBFMProcessingStep
            progress={analysisProgress}
            onCancel={handleCancelAnalysis}
          />
        );

      case 'results':
        return (
          <DRBFMResultsStep
            results={analysisResults}
            onExport={handleExport}
            onPreview={handlePreview}
            onNewAnalysis={handleNewAnalysis}
            isExporting={isExporting}
            exportError={exportError}
            drbfmSessionId={analysisResults?.session?.id}
            drbfmItemName={analysisResults?.session?.itemName}
            onLinkCad={handleLinkCad}
            isLinkingCad={isLinkingCad}
            linkCadError={linkCadError}
            cadEnabled={cadEnabled}
            onReviewDesignChange={handleReviewDesignChange}
            onReviewFailureMode={handleReviewFailureMode}
            onPreviewFailureModeRevision={handlePreviewFailureModeRevision}
          />
        );

      default:
        return null;
    }
  };

  const firstRunEntry = mainlineEnabled && !resultsOpen && !mainlineOpen && !isCaseMode
    && workflowMode === 'quick' && wizardStep === 'input' && searchParams.get('input') !== 'legacy';
  return (
    <div className={`mx-auto max-w-[1200px] px-6 pt-6 ${firstRunEntry ? 'pb-0' : 'pb-6'}`}>
      {!firstRunEntry && <DRBFMPageHeader
        hideActions={isQuickProcessing}
        resultsOpen={resultsOpen}
        onNewAnalysis={() => navigate('/drbfm')}
        backLabel={
          sessionIdParam || wizardStep === 'results'
            ? t('drbfm.backToHistory', '分析履歴に戻る')
            : t('common.backToDashboard', 'ダッシュボードに戻る')
        }
        onBack={handleBackClick}
        isCaseMode={isCaseMode}
        advancedModesEnabled={advancedModesEnabled}
        mainlineEnabled={mainlineEnabled}
        mainlineOpen={mainlineOpen}
        onOpenMainline={() => {
          const sid = sessionIdParam || analysisResults?.session?.id;
          if (!mainlineOpen) {
            navigate(`/drbfm?edit=1${sid ? `&session=${sid}` : ''}`);
          } else if (!sid) {
            navigate('/drbfm');
          } else if (!mainlineEnabled) {
            navigate(sessionResultsPath(sid, null, false));
          } else {
            // Opening the editor does not enrol a session, so its kind is read; an unreadable one keeps the existing view.
            void drbfmService.getSession(sid).catch(() => null)
              .then((session) => navigate(sessionResultsPath(sid, session, true)));
          }
        }}
        workflowMode={workflowMode}
        onWorkflowModeChange={handleWorkflowModeChange}
        onToggleCaseMode={handleCaseModeClick}
        onOpenHistory={() => navigate('/drbfm/history')}
      />}

      {!isCaseMode && error && (
        <Alert variant="destructive" className="mb-6 flex items-start gap-4">
          <AlertDescription className="revy-cjk flex-1 leading-[1.6]">{error}</AlertDescription>
          {/* Decorative, exactly like the MUI original: its onClose was a no-op,
              so the X never dismissed anything. Wrapped because Alert positions
              any direct svg child absolutely at its top-left, which would drop
              the glyph on top of the message. */}
          <span className="mt-0.5 shrink-0">
            <X className="size-4 opacity-70" strokeWidth={1.9} aria-hidden="true" />
          </span>
        </Alert>
      )}

      {completionPending && (
        <div className="mb-4 flex gap-2">
          <Button onClick={handleRetryCompletion} disabled={isLoading || isAnalyzing}>
            {t('drbfm.firstRun.retryResults')}
          </Button>
          <Button variant="outline" onClick={cancelAnalysis} disabled={isLoading || isAnalyzing}>
            {t('drbfm.firstRun.returnToInput')}
          </Button>
        </div>
      )}

      {resultsOpen && sessionIdParam ? (
        <div className="mb-6 min-h-[400px]">
          <React.Suspense fallback={<p role="status">{t('drbfm.resultsWorkspace.load.loading')}</p>}>
            <ResultsWorkspace key={`${tenantId}:${sessionIdParam}`} sessionId={sessionIdParam} status={mainlineStatus}
              summaryActions={(state) => <WorkspaceOutboundActions exportOnly sessionId={state.session.id}
                itemName={state.session.item_name} cadModelId={anchoredCadModel(state)} />} />
          </React.Suspense>
        </div>
      ) : mainlineOpen ? (
        <Card className="mb-6 min-h-[400px] p-6">
          <DRBFMMainlineWorkflow key={`${tenantId}:${sessionIdParam ?? 'new'}`}
            sessionId={sessionIdParam} tenantId={tenantId ?? ''} status={mainlineStatus}
            onSession={sid => navigate(`/drbfm?edit=1&session=${sid}`, { replace: true })} />
        </Card>
      ) : isCaseMode ? (
        <Card className="mb-6 min-h-[400px] p-6">
          <DRBFMCaseWorkflow cadEnabled={cadEnabled} onProgressChange={setCaseInProgress} />
        </Card>
      ) : workflowMode === 'staged' ? (
        <Card className="mb-6 min-h-[400px] p-6">
          <DRBFMStagedWorkflow key={`${tenantId}:${sessionIdParam ?? 'new'}`}
            resumeSessionId={sessionIdParam} onContentChange={setStagedHasContent}
            onSession={sid => navigate(`/drbfm?mode=staged&session=${sid}`, { replace: true })} />
        </Card>
      ) : (
        <>
          {!firstRunEntry && !isQuickProcessing && <Card className="mb-6 p-6">
            <Stepper
              steps={steps}
              activeStep={activeStep}
              progressLabel={(current, total) => `${current} / ${total}`}
            />
          </Card>}

          {firstRunEntry ? renderStepContent() : <Card className="mb-6 min-h-[400px] p-6">{renderStepContent()}</Card>}

          {isLoading && wizardStep === 'input' && <SessionCreatingOverlay />}

          <DrbfmPreviewDialog
            open={preview.isOpen}
            onClose={preview.close}
            isLoading={preview.isLoading}
            error={preview.error}
            html={preview.html}
          />
        </>
      )}
    </div>
  );
};

export default DRBFMPage;
